import React, { useState, useEffect } from "react";
import Card from "components/card";
import { useUnidades } from "context/UnidadeContext";
import { useFuncionarios } from "context/FuncionarioContext";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { db } from "services/firebase";
import { useAuth } from "context/AuthContext";
import { BsExclamationDiamond } from "react-icons/bs";

interface Pessoa {
  id: string;
  dispositivoId: string;
  dataHora: string;
  fotoUrl: string;
  faceId: string;
}

interface FluxoPessoasProps {
  selectedUnidadeId: string | null;
}

export default function FluxoPessoas({ selectedUnidadeId }: FluxoPessoasProps) {
  const { user } = useAuth();
  const { unidades } = useUnidades();
  const { funcionarios, adicionarFuncionario, removerFuncionario } = useFuncionarios();
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);

  const formatarDataHora = (dateTimeStr: string): [string, string] => {
    if (!dateTimeStr || !dateTimeStr.includes(" ")) return ["--/--/----", "--:--"];
    const [date, time] = dateTimeStr.split(" ");
    const [yyyy, mm, dd] = date.split("-");
    const [hh, mi] = time.split(":");
    return [`${dd}/${mm}/${yyyy}`, `${hh}:${mi}`];
  };

  const isAdicionado = (faceId: string) => funcionarios.some((f) => f.faceId === faceId);

  useEffect(() => {
    if (!user?.uid || !selectedUnidadeId) {
      setPessoas([]);
      return;
    }

    const unidade = unidades.find((u) => u.id === selectedUnidadeId);
    if (!unidade) {
      setPessoas([]);
      return;
    }

    const q = query(
      collection(db, `clientes/${user.uid}/pessoas`),
      where("dispositivoId", "==", unidade.dispositivoId),
      orderBy("dataHora", "desc") 
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const lista: Pessoa[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<Pessoa, 'id'>),
      }));
      setPessoas(lista);
    });

    return () => unsub();
  }, [selectedUnidadeId, unidades, user]);

  const handleAdicionarFuncionario = async (pessoa: Pessoa, unidadeNome: string) => {
    const [data, hora] = formatarDataHora(pessoa.dataHora);
    await adicionarFuncionario({
      nome: "Desconhecido",
      unidadeName: unidadeNome,
      dataCadastro: data,
      horaCadastro: hora,
      foto: pessoa.fotoUrl,
      faceId: pessoa.faceId,
    });
  };

  return (
    <Card extra="w-full p-4 h-full">
      <h4 className="text-xl font-bold text-navy-700 mb-4">
        Fluxo de Pessoas
      </h4>

      {!selectedUnidadeId ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <div className="mt-2 flex items-center justify-center rounded-full bg-lightPrimary p-[26px] text-5xl font-bold text-[#CF4949]">
            <BsExclamationDiamond />
          </div>
          <h3 className="text-xl font-bold text-navy-700 my-3">Nada Aqui</h3>
          <p className="text-center text-sm text-gray-600 px-4">
            Selecione uma <span className="font-semibold">Unidade</span> na lista acima para visualizar o <span className="font-semibold">Fluxo de Pessoas</span>.
          </p>
        </div>
      ) : pessoas.length === 0 ? (
        <div className="flex items-center justify-center h-full text-center text-gray-500">
          Nenhuma pessoa detectada para esta unidade.
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[280px]">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sm font-bold text-gray-600">
                <th>Foto</th>
                {/* <th>Usuário</th> */}
                <th>Data</th>
                <th>Hora</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {pessoas.map((p) => {
                const [data, hora] = formatarDataHora(p.dataHora);
                const adicionado = isAdicionado(p.faceId);
                const unidade = unidades.find(u => u.id === selectedUnidadeId);

                return (
                  <tr key={p.id} className="border-t">
                    <td className="py-2">
                      <a href={p.fotoUrl} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={p.fotoUrl} 
                          alt="Pessoa detectada" 
                          className="w-8 h-8 rounded-full cursor-pointer transition-transform duration-200 hover:scale-110" 
                        />
                      </a>
                    </td>
                    {/* <td className="text-sm font-bold text-navy-700">Desconhecido</td> */}
                    <td className="text-sm font-bold text-navy-700">{data}</td>
                    <td className="text-sm font-bold text-navy-700">{hora}</td>
                    <td className="text-sm font-bold text-navy-700">
                      <button
                        onClick={() =>
                          adicionado
                            ? removerFuncionario(p.faceId)
                            : handleAdicionarFuncionario(p, unidade?.nome ?? "Desconhecido")
                        }
                        className={`text-sm font-bold ${
                          adicionado ? "text-green-600 cursor-default" : "text-blue-600 hover:underline"
                        }`}
                        disabled={adicionado}
                      >
                        {adicionado ? "Adicionado" : "Adicionar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
